import React, { memo, useMemo, useCallback, useEffect, useState } from "react";
import { Box, List, ListItem, ListItemText, ListItemAvatar, Avatar, Typography, TextField, InputAdornment, IconButton, Badge, Chip, Divider, Tooltip, CircularProgress } from "@mui/material";
import { Search, Chat, MoreVert, Check, DoneAll, Schedule, Person, AttachFile, Image as ImageIcon, InsertDriveFile, Videocam, MusicNote } from "@mui/icons-material";

const normalizePhoneNumber = (number) => {
  if (!number) return '';
  const cleaned = number.toString().replace(/\D/g, '');
  return cleaned.startsWith('91') ? `+${cleaned}` : `+91${cleaned}`;
};

const formatPhoneNumber = (number) => {
  if (!number) return '';
  const cleaned = number.toString().replace(/\D/g, '');
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return number;
};

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  if (isNaN(date.getTime()) || date.getFullYear() < 2000 || date.getFullYear() > now.getFullYear() + 1) {
    return '';
  }
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (isYesterday) {
    return 'Yesterday';
  } else {
    const currentYear = now.getFullYear();
    const messageYear = date.getFullYear();
    if (currentYear === messageYear) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};

const getMessagePreview = (lastMessage, lastMessageType, isInbound) => {
  if (!lastMessage && !lastMessageType) return 'No messages yet';
  const iconProps = { fontSize: 16, color: '#666' };
  switch (lastMessageType) {
    case 'image':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ImageIcon sx={iconProps} />
          <Typography component="span" sx={{ fontSize: 'inherit' }}>Photo</Typography>
        </Box>
      );
    case 'document':
      const docMatch = lastMessage?.match(/Document: (.+)/) || lastMessage?.match(/\[Document: (.+?)\]/);
      const docName = docMatch ? docMatch[1] : 'Document';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InsertDriveFile sx={iconProps} />
          <Typography component="span" sx={{ fontSize: 'inherit' }}>{docName}</Typography>
        </Box>
      );
    case 'video':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Videocam sx={iconProps} />
          <Typography component="span" sx={{ fontSize: 'inherit' }}>Video</Typography>
        </Box>
      );
    case 'audio':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MusicNote sx={iconProps} />
          <Typography component="span" sx={{ fontSize: 'inherit' }}>Audio</Typography>
        </Box>
      );
    default:
      return lastMessage || 'New message';
  }
};

const getStatusIcon = (lastMessageStatus, lastMessageInbound) => {
  if (lastMessageInbound) return null;
  const iconProps = { fontSize: '12px' };
  switch (lastMessageStatus) {
    case "sending":
      return <Schedule sx={{ ...iconProps, color: 'gray' }} />;
    case "sent":
      return <Check sx={{ ...iconProps, color: 'gray' }} />;
    case "delivered":
      return <DoneAll sx={{ ...iconProps, color: 'gray' }} />;
    case "read":
      return <DoneAll sx={{ ...iconProps, color: '#34B7F1' }} />;
    case "failed":
      return <Schedule sx={{ ...iconProps, color: '#f44336' }} />;
    default:
      return <Schedule sx={{ ...iconProps, color: 'gray' }} />;
  }
};

const ChatItem = memo(({ chat, isSelected, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(chat);
  }, [chat, onSelect]);

  const formattedTime = useMemo(() => formatMessageTime(chat.lastMessageTimestamp), [chat.lastMessageTimestamp]);
  const messagePreview = useMemo(() => getMessagePreview(chat.lastMessage, chat.lastMessageType, chat.lastMessageInbound), [chat.lastMessage, chat.lastMessageType, chat.lastMessageInbound]);
  const statusIcon = useMemo(() => getStatusIcon(chat.lastMessageStatus, chat.lastMessageInbound), [chat.lastMessageStatus, chat.lastMessageInbound]);

  return (
    <ListItem
      button
      onClick={handleClick}
      sx={{
        backgroundColor: isSelected ? "#E8F5E8" : "transparent",
        "&:hover": { backgroundColor: isSelected ? "#E8F5E8" : "#F5F5F5" },
        py: 1.5,
        px: 2,
        borderRadius: 1,
        transition: "all 0.2s ease",
        cursor: "pointer"
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ width: 40, height: 40, backgroundColor: "#DDD", color: "#666" }}>
          <Person />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontWeight: chat.unreadCount > 0 ? 600 : 400,
                fontSize: "0.95rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                color: chat.isBlocked ? "#666" : "#111"
              }}
            >
              {chat.name || formatPhoneNumber(chat.number)}
            </Typography>
            {chat.isBlocked && (
              <Chip
                label="Blocked"
                size="small"
                color="error"
                sx={{ fontSize: "0.65rem", height: 16 }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: chat.isTyping ? "#00897B" : "#666",
                fontStyle: chat.isTyping ? "italic" : "normal",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {chat.isTyping ? "typing..." : (
                <>
                  {statusIcon}
                  {messagePreview}
                </>
              )}
            </Typography>
          </Box>
        }
      />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, minWidth: "fit-content" }}>
        <Typography sx={{ fontSize: "0.75rem", color: "#666", fontWeight: chat.unreadCount > 0 ? 500 : 400 }}>
          {formattedTime}
        </Typography>
        {chat.unreadCount > 0 && (
          <Chip
            label={chat.unreadCount > 99 ? "99+" : chat.unreadCount}
            size="small"
            sx={{
              backgroundColor: "#25D366",
              color: "white",
              fontSize: "0.7rem",
              height: 20,
              minWidth: 20,
              "& .MuiChip-label": { px: 0.75 }
            }}
          />
        )}
      </Box>
    </ListItem>
  );
});

ChatItem.displayName = 'ChatItem';

const ChatList = memo(({ chats, selectedChat, onChatSelect, searchTerm, onSearchChange, onNewChatClick, onMenuClick, hotel, isLoading, messageStatuses = {} }) => {
  const processedChats = useMemo(() => {
    let filtered = chats || [];
    
    if (Object.keys(messageStatuses).length > 0) {
      filtered = filtered.map(chat => {
        const chatKey = chat.number || chat.normalizedNumber;
        const statusUpdate = messageStatuses[chatKey];
        if (statusUpdate) {
          return {
            ...chat,
            lastMessageStatus: statusUpdate.status,
            lastMessageTimestamp: statusUpdate.timestamp || chat.lastMessageTimestamp
          };
        }
        return chat;
      });
    }
  
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(chat => {
        const name = chat.name?.toLowerCase() || '';
        const normalizedNumber = normalizePhoneNumber(chat.number);
        const formattedNumber = formatPhoneNumber(chat.number);
        const rawNumber = chat.number?.toString() || '';
        const lastMessage = chat.lastMessage?.toLowerCase() || '';
        return (
          name.includes(searchLower) ||
          normalizedNumber.toLowerCase().includes(searchLower) ||
          formattedNumber.toLowerCase().includes(searchLower) ||
          rawNumber.includes(searchLower) ||
          lastMessage.includes(searchLower)
        );
      });
    }
  
    // Fixed sorting logic - sort by lastMessageTimestamp in descending order
    return filtered.sort((a, b) => {
      const aTime = a.lastMessageTimestamp || 0;
      const bTime = b.lastMessageTimestamp || 0;
      return bTime - aTime; // Most recent first
    });
  }, [chats, searchTerm, messageStatuses]);

  return (
    <Box sx={{ width: { xs: "100%", md: "30%" }, borderRight: { md: "1px solid #E0E0E0" }, display: "flex", flexDirection: "column", height: "100%", backgroundColor: "white", minWidth: { md: "350px" } }}>
      {/* Header */}
      <Box sx={{ backgroundColor: "#00897B", p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #E0E0E0" }}>
        <Typography sx={{ color: "white", fontSize: { xs: ".9rem", md: "1rem" }, fontWeight: 600, flex: 1, textAlign: "left" }}>
          WhatsApp
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="New Chat">
            <IconButton onClick={onNewChatClick} sx={{ p: 1 }}>
              <Chat sx={{ fontSize: "1.4rem", color: "#FFFFFF" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Menu">
            <IconButton onClick={onMenuClick} sx={{ p: 1 }}>
              <MoreVert sx={{ fontSize: "1.4rem", color: "#FFFFFF" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ p: 1.5, backgroundColor: "#F0F0F0" }}>
        <TextField
          fullWidth
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" sx={{ fontSize: "1.2rem" }} />
              </InputAdornment>
            ),
            sx: { backgroundColor: "white", borderRadius: "20px", fontSize: "0.9rem" }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "2px solid #00897B" }
            }
          }}
        />
      </Box>

      {/* Chat List */}
      <Box sx={{ flex: 1, overflow: "hidden", backgroundColor: "white" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress size={32} sx={{ color: "#00897B" }} />
          </Box>
        ) : processedChats.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "300px", textAlign: "center", px: 3 }}>
            <Chat sx={{ fontSize: "3rem", color: "#DDD", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {searchTerm ? "No chats found" : "No conversations yet"}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {searchTerm ? "Try searching with a different phone number or message" : "Start messaging to see your conversations here"}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, overflow: "auto", height: "100%", "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.2)", borderRadius: "3px" } }}>
            {processedChats.map((chat, index) => (
              <div key={`${chat.id}-${chat.number}`}>
                <ChatItem
                  chat={chat}
                  isSelected={
                    selectedChat?.number === chat.number ||
                    selectedChat?.normalizedNumber === chat.normalizedNumber
                  }
                  onSelect={onChatSelect}
                />
                {index < processedChats.length - 1 && (
                  <Divider sx={{ mx: 2, opacity: 0.3 }} />
                )}
              </div>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
});

ChatList.displayName = 'ChatList';
export default ChatList;